import React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { change, formValueSelector, isDirty, reset } from 'redux-form'
// hooks
import { useModalData } from 'hooks/index'
// components
import { Modal } from 'components/UI'
// view components
import SetColorModal from 'components/Modals/SetColorModal'
import ResultTargetForm from './Form'
// constants
import { Modals } from 'constants/index'
// context
import { KPIContext } from 'context/index'
// utils
import { handleError } from 'utils/errors/handlers'
import { get, isEmpty, isFunction } from 'lodash'
import { generateRanges, getFieldId } from 'utils/index'
import { getResultTargetData, getResultTargetInitialData } from 'utils/kpi/resultTargets'

const PAPER_PROPS = {
  style: {
    margin: 0,
    overflow: 'hidden'
  }
}

interface Props {
  targetForm: string
  targetField?: string
  displayField?: string
  isResultsList?: boolean
}

const KPIResultTargetsModal: React.FC<Props> = ({ targetForm, targetField, displayField, isResultsList }) => {
  const dispatch = useDispatch()

  const {
    hideModal,
    modalData: { isOpen, isCreateForm, closePopup, clearZoom }
  } = useModalData({ modalType: Modals.KPI_RESULTS_MODAL })

  const [isLoading, setLoading] = React.useState(false)

  const { entity, update } = React.useContext<any>(KPIContext)

  // redux
  const selector = formValueSelector(targetForm)

  const { isFormDirty, recurrence, resultInputType, resultTarget, resultTypeScale } = useSelector((state) => ({
    isFormDirty: isDirty(targetForm)(state),
    recurrence: selector(state, 'recurrence') || {},
    resultInputType: selector(state, 'resultInputType'),
    resultTarget: selector(state, 'resultTarget'),
    resultTypeScale: selector(state, 'resultTypeScale')
  }))

  // constants
  const source = isResultsList ? get(entity, 'resultTarget') : resultTarget
  const differentResultsData = get(source, 'differentResults', [])
  const resultsData = get(source, 'results', [])
  const defaultResultsData = get(source, 'defaultResults', [])
  const shouldShowDifferentTargets = get(source, 'differentResults')
  const resultType = get(resultInputType, 'label', '').toUpperCase()
  const scaleValue = resultTypeScale || get(entity, 'resultTypeScale', [])

  const differentResults = shouldShowDifferentTargets
    ? isEmpty(source.differentResults)
      ? generateRanges({ recurrence } as any).ranges.map((value) => ({
          ...value,
          results: []
        }))
      : source.differentResults
    : undefined

  const initialValues = {
    differentTargets: get(source, 'differentTargets', false),
    results: getResultTargetInitialData(resultsData, resultType, scaleValue),
    differentResults: differentResultsData.map((period) => {
      return {
        ...period,
        results: getResultTargetInitialData(period.results, resultType, scaleValue)
      }
    }),
    defaultResults: getResultTargetInitialData(defaultResultsData, resultType, scaleValue)
  }

  // methods
  const changeField = (field, value) => dispatch(change(targetForm, field, value))

  const handleCloseModal = () => {
    hideModal()

    if (isFunction(clearZoom)) {
      clearZoom()
    }
  }

  const handleSubmit = async (data) => {
    try {
      setLoading(true)

      const resultTarget = {
        capturedResults: get(source, 'capturedResults', []),
        differentResults: data.differentResults.map((period) => ({
          ...period,
          results: getResultTargetData(period.results, resultType)
        })),
        differentTargets: data.differentTargets,
        results: getResultTargetData(data.results, resultType),
        defaultResults: getResultTargetData(data.defaultResults, resultType)
      }

      await update({
        variables: {
          id: entity.id,
          input: {
            fieldValues: [{ fieldId: getFieldId(entity.fields, 'resultTarget'), value: resultTarget }],
            resultTarget: resultTarget
          }
        }
      })

      if (isFunction(clearZoom)) {
        clearZoom()
      }

      dispatch(reset(targetForm))

      hideModal()
    } catch (error) {
      handleError(error, 'KPIResultTargetsModal.handleSubmit')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdate = (data) => {
    changeField(targetField, {
      capturedResults: get(source, 'capturedResults', []),
      differentResults: get(differentResults, 'data', []).map((period) => ({
        ...period,
        results: getResultTargetData(period.results, resultType)
      })),
      differentTargets: data.differentTargets,
      results: getResultTargetData(data.results, resultType),
      defaultResults: getResultTargetData(data.defaultResults, resultType)
    })

    changeField(displayField, 'Modify Result Targets')

    hideModal()
  }

  const handleFormSubmit = isCreateForm || isFormDirty ? handleUpdate : handleSubmit

  React.useEffect(() => closePopup && closePopup(), [closePopup])

  return (
    <Modal width={'md'} isOpen={isOpen} disableRestoreFocus PaperProps={PAPER_PROPS}>
      <ResultTargetForm
        width={'md'}
        isLoading={isLoading}
        targetForm={targetForm}
        onSubmit={handleFormSubmit}
        hideModal={handleCloseModal}
        initialValues={initialValues}
      />
      <SetColorModal />
    </Modal>
  )
}

export default KPIResultTargetsModal
